"use client";

import { useCallback, useMemo, useState, useTransition } from "react";
import { AlertCircle, Loader2, ShieldCheck } from "lucide-react";
import Turnstile from "react-turnstile";
import { cn } from "@/lib/utils";
import {
  clearTurnstileSessionAction,
  createTurnstileSessionAction,
} from "@/actions/security/turnstile-session";

interface TurnstileChallengeProps {
  className?: string;
  title?: string;
  helperText?: string;
  compact?: boolean;
}

const emitTokenIssued = (expiresAt?: number) => {
  if (typeof document === "undefined") {
    return;
  }

  document.dispatchEvent(
    new CustomEvent("turnstile:token:issued", {
      detail: { expiresAt },
    }),
  );
};

const emitTokenInvalidated = (reason?: string) => {
  if (typeof document === "undefined") {
    return;
  }

  document.dispatchEvent(
    new CustomEvent("turnstile:token:invalidated", {
      detail: { reason },
    }),
  );
};

export function TurnstileChallenge({
  className,
  title = "Verify you are human",
  helperText = "Complete the challenge to run this tool.",
  compact = false,
}: TurnstileChallengeProps) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const [widgetKey, setWidgetKey] = useState(() => Date.now());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [isProcessing, startTransition] = useTransition();

  const invalidateSession = useCallback(
    (reason?: string, options: { rotateWidget?: boolean; notifyServer?: boolean } = {}) => {
      setHasActiveSession(false);
      setErrorMessage(reason ?? null);
      emitTokenInvalidated(reason);

      if (options.notifyServer) {
        void clearTurnstileSessionAction();
      }

      if (options.rotateWidget !== false) {
        setWidgetKey((previous) => previous + 1);
      }
    },
    [],
  );
  const handleSessionIssued = useCallback((expiresAt?: number) => {
    setErrorMessage(null);
    setHasActiveSession(true);
    emitTokenIssued(expiresAt);
  }, []);

  const statusIcon = useMemo(() => {
    if (errorMessage) {
      return <AlertCircle className="size-4 text-destructive" />;
    }

    if (hasActiveSession) {
      return <ShieldCheck className="size-4 text-emerald-500" />;
    }

    if (isProcessing) {
      return <Loader2 className="size-4 animate-spin text-muted-foreground" />;
    }

    return <ShieldCheck className="size-4 text-muted-foreground" />;
  }, [errorMessage, hasActiveSession, isProcessing]);

  if (!siteKey) {
    return (
      <div className={cn("rounded-lg border border-dashed p-4", className)}>
        <p className="text-sm font-medium text-destructive">
          Missing NEXT_PUBLIC_TURNSTILE_SITE_KEY.
        </p>
        <p className="text-sm text-muted-foreground">
          Add your site key to enable bot protection.
        </p>
      </div>
    );
  }

  //   if (token) {
  //     return (
  //       <div className="rounded-xl border bg-card p-4">
  //         <div className="flex items-center gap-2 text-emerald-600">
  //           <ShieldCheck className="size-4" />
  //           <p className="text-sm font-medium">You are human</p>
  //         </div>
  //       </div>
  //     );
  //   }

  return (
    <div className={cn("rounded-xl border bg-card p-4", compact && "border-0 bg-transparent p-0", className)}>
      {!compact && (
        <>
          <div className="flex items-center gap-2">
            {statusIcon}
            <p className="text-sm font-medium">{title}</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{helperText}</p>
        </>
      )}
      <div className={cn(!compact && "mt-4")}>
        <Turnstile
          key={widgetKey}
          sitekey={siteKey}
          theme="auto"
          onVerify={(token) => {
            startTransition(() => {
              createTurnstileSessionAction(token)
                .then((result) => {
                  if (result.success) {
                    handleSessionIssued(result.expiresAt ?? undefined);
                  } else {
                    invalidateSession(
                      result.error ?? "Verification failed. Retry in a moment.",
                      { notifyServer: true },
                    );
                  }
                })
                .catch(() => {
                  invalidateSession("Unable to verify Turnstile challenge.", {
                    notifyServer: true,
                  });
                });
            });
          }}
          onExpire={() => {
            invalidateSession("Challenge expired. Please try again.", {
              notifyServer: true,
            });
          }}
          onError={(error) => {
            invalidateSession(error ?? "Verification failed. Retry in a moment.", {
              notifyServer: true,
            });
          }}
          onTimeout={() => {
            invalidateSession("Challenge timed out. Please retry.", {
              notifyServer: true,
            });
          }}
          refreshExpired="auto"
        />
        {errorMessage ? (
          <p className="mt-2 text-xs text-destructive">{errorMessage}</p>
        ) : null}
      </div>
    </div>
  );
}
